import React, { useState, useEffect, useCallback } from 'react';
import {
  Lock, Trash2, LogOut, Users, UserX, UserCheck, Settings, User,
  Search, Shield, Activity, BarChart3, Tv, MessageCircle, Star, BookOpen,
  Eye, ChevronDown, ChevronRight, X, AlertTriangle, Clock, UserPlus,
  Gamepad2, Trophy, RotateCcw, Flag, Zap, RefreshCw, Globe, Plus, Crown, Award, Mail, TrendingUp, CheckCircle2
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import axios from 'axios';
import { API_URL } from '../config/api';

// ==================== USER DETAIL MODAL ====================
const UserDetailModal = ({ userId, isOpen, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && userId) {
      setLoading(true);
      axios.get(`${API_URL}/api/admin/users/${userId}/activity`)
        .then(res => setData(res.data))
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [isOpen, userId]);

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-zinc-900 text-white border-zinc-700" data-testid="user-detail-modal">
        <DialogTitle className="sr-only">User Details</DialogTitle>
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin h-6 w-6 border-2 border-red-500 border-t-transparent rounded-full" />
          </div>
        ) : data ? (
          <div>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-full bg-red-600 flex items-center justify-center text-white text-xl font-bold overflow-hidden">
                {data.user.profile_picture ? (
                  <img src={data.user.profile_picture} alt="" className="w-full h-full object-cover" />
                ) : (
                  `${data.user.first_name?.[0] || ''}${data.user.last_name?.[0] || ''}`
                )}
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">
                  {data.user.first_name} {data.user.last_name}
                </h3>
                <p className="text-gray-400 text-sm">@{data.user.username || 'no-username'}</p>
                <p className="text-gray-500 text-xs">{data.user.email}</p>
              </div>
              <div className="ml-auto">
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  data.user.role === 'admin' ? 'bg-purple-600/20 text-purple-400' : 'bg-blue-600/20 text-blue-400'
                }`}>
                  {data.user.role || 'user'}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-3 mb-6">
              {[
                { label: 'Chronicle', val: data.stats.diary_entries, icon: BookOpen },
                { label: 'Ratings', val: data.stats.ratings, icon: Star },
                { label: 'TV Tracked', val: data.stats.tv_tracking, icon: Tv },
                { label: 'Comments', val: data.stats.comments, icon: MessageCircle },
                { label: 'Reviews', val: data.stats.reviews, icon: Activity },
                { label: 'Clubs', val: data.stats.clubs, icon: Users },
                { label: 'Followers', val: data.stats.followers, icon: UserPlus },
                { label: 'Following', val: data.stats.following, icon: Eye },
              ].map(s => (
                <div key={s.label} className="bg-zinc-800 rounded-lg p-3 text-center">
                  <s.icon className="h-4 w-4 text-gray-500 mx-auto mb-1" />
                  <p className="text-white font-bold text-lg">{s.val}</p>
                  <p className="text-gray-500 text-xs">{s.label}</p>
                </div>
              ))}
            </div>

            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Status</span>
                <span className={data.user.is_active !== false ? 'text-green-400' : 'text-red-400'}>
                  {data.user.is_active !== false ? 'Active' : 'Disabled'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Joined</span>
                <span className="text-white">{new Date(data.user.created_at).toLocaleDateString()}</span>
              </div>
              {data.user.bio && (
                <div>
                  <span className="text-gray-400 block mb-1">Bio</span>
                  <p className="text-white text-sm bg-zinc-800 rounded p-2">{data.user.bio}</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <p className="text-center text-gray-500 py-8">Failed to load user data</p>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ==================== CLUB MEMBERS MODAL ====================
const ClubMembersModal = ({ club, isOpen, onClose }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && club) {
      setLoading(true);
      axios.get(`${API_URL}/api/admin/clubs/${club.id}/members`)
        .then(res => setMembers(res.data))
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [isOpen, club]);

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-zinc-900 text-white border-zinc-700" data-testid="club-members-modal">
        <DialogTitle className="text-lg font-bold text-white mb-4">
          {club?.name} - Members ({members.length})
        </DialogTitle>
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin h-5 w-5 border-2 border-red-500 border-t-transparent rounded-full" />
          </div>
        ) : members.length > 0 ? (
          <div className="max-h-80 overflow-y-auto space-y-2">
            {members.map(m => (
              <div key={m.user_id} className="flex items-center gap-3 p-2 bg-zinc-800 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-red-600 flex items-center justify-center text-white text-xs font-medium overflow-hidden">
                  {m.user?.profile_picture ? (
                    <img src={m.user.profile_picture} alt="" className="w-full h-full object-cover" />
                  ) : (
                    `${m.user?.first_name?.[0] || ''}${m.user?.last_name?.[0] || ''}`
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">
                    {m.user?.first_name} {m.user?.last_name}
                  </p>
                  <p className="text-gray-500 text-xs">{m.user?.email}</p>
                </div>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                  m.role === 'owner' ? 'bg-yellow-600/20 text-yellow-400' :
                  m.role === 'admin' ? 'bg-purple-600/20 text-purple-400' :
                  'bg-zinc-700 text-gray-400'
                }`}>
                  {m.role}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">No members found</p>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ==================== ADMIN PAGE ====================
const AdminPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [adminUser, setAdminUser] = useState(null);
  const [loginError, setLoginError] = useState('');
  const [email, setEmail] = useState('support@casitaplex.tv');
  const [password, setPassword] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [users, setUsers] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [stats, setStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedClub, setSelectedClub] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // {type, id, name, action}

  // Daily Games Admin State
  const [gameData, setGameData] = useState(null);
  const [gameHistory, setGameHistory] = useState([]);
  const [gameLeaderboard, setGameLeaderboard] = useState([]);
  const [fairnessFlags, setFairnessFlags] = useState([]);
  const [winnersCircle, setWinnersCircle] = useState(null);
  const [winnersLoading, setWinnersLoading] = useState(false);
  const [awardingUser, setAwardingUser] = useState(null);
  const [winnersTab, setWinnersTab] = useState('yesterday');
  const [winnerRecords, setWinnerRecords] = useState([]);
  const [gameSubTab, setGameSubTab] = useState('today');
  const [leaderboardPeriod, setLeaderboardPeriod] = useState('all');
  const [gameLoading, setGameLoading] = useState(false);

  // Movie Xchange admin state
  const [mStockSearch, setMStockSearch] = useState('');
  const [mStockEvents, setMStockEvents] = useState([]);
  const [mStockExpandedEvent, setMStockExpandedEvent] = useState(null);
  const [mStockSearching, setMStockSearching] = useState(false);
  const [mStockGames, setMStockGames] = useState([]);
  const [mStockCreating, setMStockCreating] = useState(null);
  const [mStockResolving, setMStockResolving] = useState(null);

  // Prize Management state
  const [prizes, setPrizes] = useState([]);
  const [prizeUserSearch, setPrizeUserSearch] = useState('');
  const [prizeUserResults, setPrizeUserResults] = useState([]);
  const [prizeForm, setPrizeForm] = useState(() => {
    const now = new Date();
    now.setMonth(now.getMonth() - 1);
    const prevMonth = now.toISOString().slice(0, 7);
    return { user_id: '', user_display: '', month: prevMonth, placement: '', gift_card_id: '', gift_card_pin: '', gift_card_value: '', notes: '', override: false };
  });
  const [prizeAwarding, setPrizeAwarding] = useState(false);
  const [prizeFilter, setPrizeFilter] = useState({ month: '', placement: '', status: '' });
  const [prizeDrafts, setPrizeDrafts] = useState([]);
  const [loadingDrafts, setLoadingDrafts] = useState(false);
  const PRIZE_TEMPLATES = { '1ST': 50, '2ND': 35, '3RD': 25 };

  const loadMStockGames = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/movie-stock/admin/games`);
      setMStockGames(res.data.games || []);
    } catch (err) { console.error('Failed to load Movie Xchange games:', err); }
  }, []);

  const searchMStockMarkets = async (q) => {
    setMStockSearching(true);
    try {
      const res = await axios.get(`${API_URL}/api/movie-stock/admin/search-markets?q=${encodeURIComponent(q || '')}&limit=50`);
      setMStockEvents(res.data.events || []);
    } catch (err) { console.error(err); }
    finally { setMStockSearching(false); }
  };

  const createMStockGameFromEvent = async (ev) => {
    setMStockCreating(ev.event_id);
    try {
      await axios.post(`${API_URL}/api/movie-stock/admin/create-game`, {
        title: ev.event_title,
        description: ev.event_description || '',
        image: ev.event_image || '',
        outcomes: ev.event_outcomes || [],
        outcome_images: ev.event_outcome_images || [],
        outcome_prices: ev.event_outcome_prices || [],
        expiry: ev.event_end_date || '',
        source_event_id: ev.event_id,
      });
      await loadMStockGames();
      alert('Movie Xchange game created!');
    } catch (err) { alert('Failed: ' + (err.response?.data?.detail || err.message)); }
    finally { setMStockCreating(null); }
  };

  const resolveMStockGame = async (gameId, winningOutcome) => {
    if (!window.confirm(`Resolve game with winner: "${winningOutcome}"?`)) return;
    setMStockResolving(gameId);
    try {
      const res = await axios.post(`${API_URL}/api/movie-stock/admin/resolve/${gameId}`, { winning_outcome: winningOutcome });
      alert(`Resolved! ${res.data.winners} winners, ${res.data.losers} losers. Top score: ${res.data.top_score}`);
      await loadMStockGames();
    } catch (err) { alert('Failed: ' + (err.response?.data?.detail || err.message)); }
    finally { setMStockResolving(null); }
  };

  const deleteMStockGame = async (gameId) => {
    if (!window.confirm('Delete this game and refund all picks?')) return;
    try {
      const res = await axios.delete(`${API_URL}/api/movie-stock/admin/games/${gameId}`);
      alert(`Deleted! ${res.data.refunded_picks} picks refunded`);
      await loadMStockGames();
    } catch (err) { alert('Failed: ' + (err.response?.data?.detail || err.message)); }
  };

  // Prize Management functions
  const loadPrizes = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (prizeFilter.month) params.set('month', prizeFilter.month);
      if (prizeFilter.placement) params.set('placement', prizeFilter.placement);
      if (prizeFilter.status) params.set('status', prizeFilter.status);
      const res = await axios.get(`${API_URL}/api/admin/prizes?${params.toString()}`);
      setPrizes(res.data.prizes || []);
    } catch (err) { console.error(err); }
  }, [prizeFilter]);

  const searchPrizeUsers = async (q) => {
    setPrizeUserSearch(q);
    if (q.length < 2) { setPrizeUserResults([]); return; }
    try {
      const res = await axios.get(`${API_URL}/api/admin/prizes/search-users?q=${encodeURIComponent(q)}`);
      setPrizeUserResults(res.data.users || []);
    } catch (err) { console.error(err); }
  };

  const handleAwardPrize = async () => {
    if (!prizeForm.user_id || !prizeForm.placement || !prizeForm.gift_card_id || !prizeForm.gift_card_value) {
      alert('Please fill all required fields');
      return;
    }
    setPrizeAwarding(true);
    try {
      await axios.post(`${API_URL}/api/admin/prizes/award`, {
        user_id: prizeForm.user_id,
        month: prizeForm.month,
        placement: prizeForm.placement,
        gift_card_id: prizeForm.gift_card_id,
        gift_card_pin: prizeForm.gift_card_pin || '',
        gift_card_value: parseFloat(prizeForm.gift_card_value),
        notes: prizeForm.notes,
        override: prizeForm.override,
      });
      alert('Prize awarded successfully! Email & notification sent.');
      const resetMonth = new Date(); resetMonth.setMonth(resetMonth.getMonth() - 1);
      setPrizeForm({ user_id: '', user_display: '', month: resetMonth.toISOString().slice(0, 7), placement: '', gift_card_id: '', gift_card_pin: '', gift_card_value: '', notes: '', override: false });
      setPrizeUserSearch('');
      setPrizeUserResults([]);
      await loadPrizes();
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    } finally { setPrizeAwarding(false); }
  };

  const handleUpdatePrizeStatus = async (prizeId, status) => {
    try {
      await axios.put(`${API_URL}/api/admin/prizes/${prizeId}/status`, { status });
      await loadPrizes();
    } catch (err) { alert('Failed: ' + (err.response?.data?.detail || err.message)); }
  };

  const handleAutoPopulate = async () => {
    const month = prizeForm.month;
    if (!month) { alert('Select a month first'); return; }
    setLoadingDrafts(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/prizes/auto-populate?month=${month}`);
      setPrizeDrafts(res.data.drafts || []);
      if (!res.data.drafts?.length) alert('No leaderboard data found for this month');
    } catch (err) { alert('Failed: ' + (err.response?.data?.detail || err.message)); }
    finally { setLoadingDrafts(false); }
  };

  const handleAwardDraft = async (draft, giftCardId) => {
    if (!giftCardId?.trim()) { alert('Enter a gift card code'); return; }
    setPrizeAwarding(true);
    try {
      await axios.post(`${API_URL}/api/admin/prizes/award`, {
        user_id: draft.user_id,
        month: prizeForm.month,
        placement: draft.placement,
        gift_card_id: giftCardId,
        gift_card_value: draft.default_value,
        notes: `Auto-populated: ${draft.total_score} pts, ${draft.games_played} games`,
        override: false,
      });
      // Refresh drafts to mark as awarded
      const res = await axios.get(`${API_URL}/api/admin/prizes/auto-populate?month=${prizeForm.month}`);
      setPrizeDrafts(res.data.drafts || []);
      await loadPrizes();
    } catch (err) { alert('Failed: ' + (err.response?.data?.detail || err.message)); }
    finally { setPrizeAwarding(false); }
  };

  const loadDashboardData = useCallback(async () => {
    try {
      const [statsRes, usersRes, clubsRes, activityRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`),
        axios.get(`${API_URL}/api/admin/users`),
        axios.get(`${API_URL}/api/admin/clubs`),
        axios.get(`${API_URL}/api/admin/recent-activity`),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setClubs(clubsRes.data);
      setRecentActivity(activityRes.data);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    }
  }, []);

  const loadGameData = useCallback(async () => {
    setGameLoading(true);
    try {
      const [todayRes, histRes, fairRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/games/today`),
        axios.get(`${API_URL}/api/admin/games/history?limit=30`),
        axios.get(`${API_URL}/api/admin/games/fairness`),
      ]);
      setGameData(todayRes.data);
      setGameHistory(histRes.data);
      setFairnessFlags(fairRes.data);
    } catch (err) { console.error('Failed to load game data:', err); }
    finally { setGameLoading(false); }
  }, []);

  const loadLeaderboard = useCallback(async (period) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/games/leaderboard?period=${period}`);
      setGameLeaderboard(res.data);
    } catch (err) { console.error('Failed to load leaderboard:', err); }
  }, []);

  const loadWinnersCircle = useCallback(async () => {
    setWinnersLoading(true);
    try {
      const [wcRes, wrRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/games/winners-circle`),
        axios.get(`${API_URL}/api/admin/games/winner-records`),
      ]);
      setWinnersCircle(wcRes.data);
      setWinnerRecords(wrRes.data.records || []);
    } catch (err) { console.error('Failed to load winners circle:', err); }
    finally { setWinnersLoading(false); }
  }, []);

  const handleAwardWinner = async (userId, period) => {
    setAwardingUser(userId);
    try {
      await axios.post(`${API_URL}/api/admin/games/award-winner?user_id=${userId}&period=${period}`);
      alert(`${period} award email sent!`);
    } catch (err) { alert('Failed: ' + (err.response?.data?.detail || err.message)); }
    finally { setAwardingUser(null); }
  };

  const handleMarkPrizeSent = async (recordId) => {
    try {
      await axios.post(`${API_URL}/api/admin/games/winner-records/${recordId}/mark-sent`);
      setWinnerRecords(prev => prev.map(r => r.id === recordId ? { ...r, prize_sent: true } : r));
    } catch (err) { alert('Failed: ' + (err.response?.data?.detail || err.message)); }
  };

  useEffect(() => {
    const savedAuth = sessionStorage.getItem('adminAuth');
    const savedUser = sessionStorage.getItem('adminUser');
    if (savedAuth && savedUser) {
      setIsLoggedIn(true);
      setAdminUser(JSON.parse(savedUser));
      loadDashboardData();
    }
  }, [loadDashboardData]);

  useEffect(() => {
    if (activeTab === 'games' && isLoggedIn) {
      loadGameData();
      loadLeaderboard(leaderboardPeriod);
      loadWinnersCircle();
      loadMStockGames();
      searchMStockMarkets('');
    }
  }, [activeTab, isLoggedIn, loadGameData, loadLeaderboard, leaderboardPeriod, loadWinnersCircle, loadMStockGames]);

  useEffect(() => {
    if (activeTab === 'prizes' && isLoggedIn) loadPrizes();
  }, [activeTab, isLoggedIn, loadPrizes]);

  const handleInvalidateSession = async (sessionId) => {
    if (!window.confirm('Invalidate this session? Points will be reversed.')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/games/sessions/${sessionId}`);
      loadGameData();
    } catch (err) { alert('Failed: ' + (err.response?.data?.detail || err.message)); }
  };

  const handleResetSession = async (sessionId) => {
    if (!window.confirm('Reset this session? The player can replay today\'s game.')) return;
    try {
      await axios.post(`${API_URL}/api/admin/games/sessions/${sessionId}/reset`);
      loadGameData();
    } catch (err) { alert('Failed: ' + (err.response?.data?.detail || err.message)); }
  };

  const handleGenerateTomorrow = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/admin/games/generate-tomorrow`);
      alert(res.data.message);
      loadGameData();
    } catch (err) { alert('Failed: ' + (err.response?.data?.detail || err.message)); }
  };

  const handleRefreshTodayMovie = async () => {
    if (!window.confirm('This will delete the current movie and all player sessions for today, then generate a new movie. Continue?')) return;
    try {
      const res = await axios.post(`${API_URL}/api/admin/games/refresh-today`);
      alert(res.data.message);
      loadGameData();
    } catch (err) { alert('Failed: ' + (err.response?.data?.detail || err.message)); }
  };

  const handleAdjustPoints = async (userId, points, reason) => {
    try {
      await axios.put(`${API_URL}/api/admin/games/points/${userId}?points=${points}&reason=${encodeURIComponent(reason)}`);
      loadGameData();
      loadDashboardData();
    } catch (err) { alert('Failed: ' + (err.response?.data?.detail || err.message)); }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/admin/verify-password`, {
        email: email,
        password: password
      });

      if (response.data.status === 'success') {
        sessionStorage.setItem('adminAuth', 'true');
        sessionStorage.setItem('adminUser', JSON.stringify(response.data.user));
        setAdminUser(response.data.user);
        setIsLoggedIn(true);
        loadDashboardData();
      }
    } catch (error) {
      setLoginError(error.response?.data?.detail || 'Invalid credentials. Access denied.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuth');
    sessionStorage.removeItem('adminUser');
    setIsLoggedIn(false);
    setAdminUser(null);
    setPassword('');
  };

  const handleToggleUserStatus = async (userId) => {
    try {
      const response = await axios.put(`${API_URL}/api/admin/users/${userId}/toggle-status`);
      if (response.data.status === 'success') {
        setUsers(prev => prev.map(u =>
          u.id === userId ? { ...u, is_active: response.data.is_active } : u
        ));
      }
    } catch { alert('Failed to update user status'); }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${API_URL}/api/admin/users/${userId}`);
      setUsers(prev => prev.filter(u => u.id !== userId));
      if (stats) setStats({ ...stats, total_users: stats.total_users - 1 });
    } catch { alert('Failed to delete user'); }
    setConfirmAction(null);
  };

  const handleDeleteClub = async (clubId) => {
    try {
      await axios.delete(`${API_URL}/api/admin/clubs/${clubId}`);
      setClubs(prev => prev.filter(c => c.id !== clubId));
      if (stats) setStats({ ...stats, total_clubs: stats.total_clubs - 1 });
    } catch { alert('Failed to delete club'); }
    setConfirmAction(null);
  };

  const filteredUsers = searchQuery
    ? users.filter(u =>
        `${u.first_name} ${u.last_name} ${u.email} ${u.username || ''}`.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : users;

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - d) / 1000);
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString();
  };

  const activityIcons = {
    diary: BookOpen,
    rating: Star,
    comment: MessageCircle,
    tv_tracking: Tv,
  };

  // ==================== LOGIN SCREEN ====================
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4" data-testid="admin-login-page">
        <div className="bg-zinc-900 rounded-2xl p-8 w-full max-w-md border border-zinc-800">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-red-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Shield className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white">Administrator Access</h1>
            <p className="text-sm mt-1" style={{ fontFamily: "'Bebas Neue', sans-serif", color: '#E50914', letterSpacing: '3px' }}>CASITAPLEX</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-gray-400 text-sm">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                placeholder="Admin email"
                required
                data-testid="admin-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-gray-400 text-sm">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                placeholder="Enter password"
                required
                autoFocus
                data-testid="admin-password-input"
              />
            </div>

            {loginError && (
              <div className="flex items-center gap-2 bg-red-900/30 border border-red-800 rounded-lg p-3">
                <AlertTriangle className="h-4 w-4 text-red-400 flex-shrink-0" />
                <p className="text-red-400 text-sm">{loginError}</p>
              </div>
            )}

            <Button
              type="submit"
              className="w-full bg-red-600 hover:bg-red-700 text-white h-11"
              disabled={loading}
              data-testid="admin-login-btn"
            >
              {loading ? 'Authenticating...' : 'Access Dashboard'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <a href="/" className="text-gray-500 hover:text-white text-sm transition-colors">
              Back to <span style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '1px' }}>CASITAPLEX</span>
            </a>
          </div>
        </div>
      </div>
    );
  }

  // ==================== ADMIN DASHBOARD ====================
  const tabs = [
    { key: 'overview', label: 'Overview', icon: BarChart3 },
    { key: 'games', label: 'Daily Games', icon: Gamepad2 },
    { key: 'prizes', label: 'Prizes', icon: Award },
    { key: 'users', label: 'Users', icon: Users, count: users.length },
    { key: 'clubs', label: 'Clubs', icon: Activity, count: clubs.length },
    { key: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-zinc-950" data-testid="admin-dashboard">
      {/* Top Bar */}
      <div className="bg-zinc-900 border-b border-zinc-800 px-6 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-1.5 bg-red-600 rounded-lg">
              <Shield className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-white text-lg" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '2px', color: '#E50914' }}>CASITAPLEX</h1>
              <p className="text-gray-500 text-xs">Admin Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-gray-400 text-sm hidden sm:block">
              {adminUser?.first_name} {adminUser?.last_name}
            </span>
            <a href="/">
              <Button size="sm" variant="outline" className="border-zinc-700 text-gray-300 hover:text-white h-8 text-xs">
                View Site
              </Button>
            </a>
            <Button
              onClick={handleLogout}
              className="bg-red-600 hover:bg-red-700 text-white h-9 px-4 text-sm font-medium"
              data-testid="admin-logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" /> Log Out
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-zinc-900 p-1 rounded-xl w-fit" data-testid="admin-tabs">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.key
                  ? 'bg-red-600 text-white shadow-lg'
                  : 'text-gray-400 hover:text-white hover:bg-zinc-800'
              }`}
              data-testid={`admin-tab-${tab.key}`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
              {tab.count !== undefined && (
                <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                  activeTab === tab.key ? 'bg-red-500 text-white' : 'bg-zinc-800 text-gray-500'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ==================== OVERVIEW TAB ==================== */}
        {activeTab === 'overview' && stats && (
          <div className="space-y-6" data-testid="admin-overview">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {[
                { label: 'Total Users', val: stats.total_users, icon: Users, color: 'text-blue-400 bg-blue-600/10' },
                { label: 'Active Users', val: stats.active_users, icon: UserCheck, color: 'text-green-400 bg-green-600/10' },
                { label: 'Total Clubs', val: stats.total_clubs, icon: Activity, color: 'text-purple-400 bg-purple-600/10' },
                { label: 'TV Tracking', val: stats.total_tv_tracking, icon: Tv, color: 'text-yellow-400 bg-yellow-600/10' },
                { label: 'Signups (7d)', val: stats.recent_signups_7d, icon: UserPlus, color: 'text-red-400 bg-red-600/10' },
              ].map(s => (
                <div key={s.label} className="bg-zinc-900 rounded-xl p-4 border border-zinc-800" data-testid={`stat-${s.label.toLowerCase().replace(/[^a-z]/g, '-')}`}>
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${s.color}`}>
                    <s.icon className="h-5 w-5" />
                  </div>
                  <p className="text-2xl font-bold text-white">{s.val}</p>
                  <p className="text-gray-500 text-xs mt-1">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Content Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { label: 'Diary Entries', val: stats.total_diary_entries, icon: BookOpen },
                { label: 'Ratings', val: stats.total_ratings, icon: Star },
                { label: 'Reviews', val: stats.total_reviews, icon: MessageCircle },
                { label: 'EP Comments', val: stats.total_comments, icon: MessageCircle },
              ].map(s => (
                <div key={s.label} className="bg-zinc-900 rounded-xl p-4 border border-zinc-800">
                  <div className="flex items-center gap-2 mb-1">
                    <s.icon className="h-4 w-4 text-gray-500" />
                    <span className="text-gray-500 text-xs">{s.label}</span>
                  </div>
                  <p className="text-xl font-bold text-white">{s.val}</p>
                </div>
              ))}
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              {/* Recent Signups */}
              <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-5">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <UserPlus className="h-4 w-4 text-red-500" /> Recent Signups
                </h3>
                <div className="space-y-3">
                  {(stats.recent_users || []).slice(0, 6).map(u => (
                    <div key={u.id} className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-red-600 flex items-center justify-center text-white text-xs font-medium flex-shrink-0">
                        {u.first_name?.[0]}{u.last_name?.[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm truncate">{u.first_name} {u.last_name}</p>
                        <p className="text-gray-500 text-xs truncate">{u.email}</p>
                      </div>
                      <span className="text-gray-600 text-xs flex-shrink-0">{formatTime(u.created_at)}</span>
                    </div>
                  ))}
                  {(!stats.recent_users || stats.recent_users.length === 0) && (
                    <p className="text-gray-600 text-sm text-center py-4">No recent signups</p>
                  )}
                </div>
              </div>

              {/* Recent Activity */}
              <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-5">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <Activity className="h-4 w-4 text-red-500" /> Recent Activity
                </h3>
                <div className="space-y-3">
                  {recentActivity.slice(0, 8).map((a, i) => {
                    const Icon = activityIcons[a.type] || Activity;
                    return (
                      <div key={i} className="flex items-start gap-3">
                        <div className="w-7 h-7 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Icon className="h-3.5 w-3.5 text-gray-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-300">
                            <span className="text-white font-medium">
                              {a.user?.username || `${a.user?.first_name || ''} ${a.user?.last_name || ''}`.trim() || 'User'}
                            </span>
                            {' '}{a.description}
                          </p>
                          {a.detail && <p className="text-gray-600 text-xs truncate">{a.detail}</p>}
                        </div>
                        <span className="text-gray-600 text-xs flex-shrink-0">{formatTime(a.timestamp)}</span>
                      </div>
                    );
                  })}
                  {recentActivity.length === 0 && (
                    <p className="text-gray-600 text-sm text-center py-4">No recent activity</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ==================== DAILY GAMES TAB ==================== */}
        {activeTab === 'games' && (
          <div className="space-y-4" data-testid="admin-games">
            {/* Sub-tabs */}
            <div className="flex gap-2 flex-wrap">
              {[
                { key: 'today', label: "Today's Game", icon: Gamepad2 },
                { key: 'movie_stock', label: 'Movie Xchange', icon: TrendingUp },
                { key: 'history', label: 'Game History', icon: Clock },
                { key: 'leaderboard', label: 'Leaderboard', icon: Trophy },
                { key: 'winners', label: 'Winners Circle', icon: Crown },
                { key: 'fairness', label: 'Fairness Flags', icon: Flag },
              ].map(t => (
                <button key={t.key} onClick={() => setGameSubTab(t.key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition ${gameSubTab === t.key ? 'bg-purple-600 text-white' : 'bg-zinc-800 text-gray-400 hover:bg-zinc-700'}`}>
                  <t.icon className="h-3.5 w-3.5" />{t.label}
                </button>
              ))}
              <button onClick={() => { loadGameData(); loadLeaderboard(leaderboardPeriod); loadWinnersCircle(); }}
                className="ml-auto flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm bg-zinc-800 text-gray-400 hover:bg-zinc-700" data-testid="games-refresh-btn">
                <RefreshCw className={`h-3.5 w-3.5 ${gameLoading ? 'animate-spin' : ''}`} />Refresh
              </button>
            </div>

            {/* TODAY'S GAME */}
            {gameSubTab === 'today' && (
              <div className="space-y-4">
                {gameData?.game ? (
                  <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-4">
                    <div className="flex items-start gap-4">
                      <img src={`https://image.tmdb.org/t/p/w200${gameData.game.poster_path}`} alt="" className="w-24 rounded-lg" />
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-bold text-white">{gameData.game.title}</h3>
                        <p className="text-sm text-gray-400">{gameData.game.date} &middot; {(gameData.game.genres || []).join(', ')}</p>
                        <div className="flex gap-4 mt-2 text-sm">
                          <span className="text-blue-400">{gameData.total_players} players</span>
                          <span className="text-green-400">{gameData.completed} completed</span>
                          <span className="text-yellow-400">{gameData.winners} winners</span>
                        </div>
                        <div className="mt-2">
                          <p className="text-xs text-gray-500 mb-1">Hints:</p>
                          {(gameData.game.hints || []).map((h, i) => (
                            <p key={i} className="text-xs text-gray-400">{i+1}. {h}</p>
                          ))}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 flex-shrink-0">
                        <button onClick={handleRefreshTodayMovie} className="flex items-center gap-1 px-3 py-1.5 bg-red-600 hover:bg-red-700 rounded-lg text-sm text-white" data-testid="refresh-today-movie-btn">
                          <RefreshCw className="h-3.5 w-3.5" />Refresh Movie
                        </button>
                        <button onClick={handleGenerateTomorrow} className="flex items-center gap-1 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm text-white" data-testid="generate-tomorrow-btn">
                          <Plus className="h-3.5 w-3.5" />Tomorrow
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6 text-center text-gray-400">
                    <p>No game generated for today</p>
                    <button onClick={handleGenerateTomorrow} className="mt-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white text-sm">Generate Game</button>
                  </div>
                )}

                {/* Player Sessions */}
                <h3 className="text-sm font-semibold text-gray-300">Player Sessions ({gameData?.sessions?.length || 0})</h3>
                <div className="space-y-2">
                  {(gameData?.sessions || []).map(s => (
                    <div key={s.id} className="bg-zinc-900 rounded-lg border border-zinc-800 p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-7 h-7 rounded-full bg-zinc-700 flex items-center justify-center text-xs text-white font-bold flex-shrink-0">
                            {(s.user?.display_name || '?')[0]}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-white truncate">{s.user?.display_name || s.user_id}</p>
                            <p className="text-xs text-gray-500">{s.user?.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${s.won ? 'bg-green-900/50 text-green-400' : s.completed ? 'bg-red-900/50 text-red-400' : 'bg-yellow-900/50 text-yellow-400'}`}>
                            {s.won ? `Won (+${s.score})` : s.completed ? 'Lost' : `${(s.attempts||[]).length}/5`}
                          </span>
                          <button onClick={() => handleResetSession(s.id)} title="Reset session" className="p-1 hover:bg-zinc-700 rounded" data-testid={`reset-session-${s.id}`}>
                            <RotateCcw className="h-3.5 w-3.5 text-blue-400" />
                          </button>
                          <button onClick={() => handleInvalidateSession(s.id)} title="Invalidate" className="p-1 hover:bg-zinc-700 rounded" data-testid={`invalidate-session-${s.id}`}>
                            <Trash2 className="h-3.5 w-3.5 text-red-400" />
                          </button>
                        </div>
                      </div>
                      {/* Guesses detail */}
                      <div className="mt-2 flex gap-1 flex-wrap">
                        {(s.attempts || []).map((a, i) => (
                          <span key={i} className={`px-2 py-0.5 rounded text-xs ${a.correct ? 'bg-green-900/40 text-green-300' : 'bg-zinc-800 text-gray-400'}`} title={`IP: ${a.ip || '?'} | ${a.user_agent?.substring(0,30) || '?'}...`}>
                            {a.guess?.substring(0, 20)}{a.guess?.length > 20 ? '...' : ''}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* MOVIE STOCK ADMIN */}
            {gameSubTab === 'movie_stock' && (
              <div className="space-y-4" data-testid="admin-movie-stock">
                {/* Search Polymarket markets */}
                <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-4 space-y-3">
                  <h4 className="text-sm font-semibold text-white flex items-center gap-2">
                    <Search className="h-4 w-4 text-emerald-400" />Search Polymarket Movie Markets
                  </h4>
                  <div className="flex gap-2">
                    <Input
                      value={mStockSearch}
                      onChange={(e) => setMStockSearch(e.target.value)}
                      placeholder="Search (e.g. Dune, Oscar, Box Office)..."
                      className="flex-1 bg-zinc-800 border-zinc-700 text-white text-sm"
                      onKeyDown={(e) => e.key === 'Enter' && searchMStockMarkets(mStockSearch)}
                      data-testid="mstock-search-input"
                    />
                    <Button onClick={() => searchMStockMarkets(mStockSearch)} disabled={mStockSearching} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1" data-testid="mstock-search-btn">
                      {mStockSearching ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
                      Search
                    </Button>
                  </div>

                  {/* Search results — grouped by event */}
                  {mStockEvents.length > 0 && (
                    <div className="max-h-[600px] overflow-y-auto space-y-2">
                      {mStockEvents.map(ev => {
                        const isExpanded = mStockExpandedEvent === ev.event_id;
                        const isEventCreated = mStockGames.some(g => g.source_event_id === ev.event_id && g.status !== 'resolved');
                        // Compute countdown
                        let countdownText = '';
                        let countdownColor = 'text-gray-500';
                        let countdownBg = 'bg-zinc-800';
                        if (ev.event_end_date) {
                          const endMs = new Date(ev.event_end_date).getTime();
                          const nowMs = Date.now();
                          const diffMs = endMs - nowMs;
                          if (diffMs <= 0) {
                            countdownText = 'Ended';
                            countdownColor = 'text-red-400';
                            countdownBg = 'bg-red-900/40';
                          } else {
                            const days = Math.floor(diffMs / 86400000);
                            const hours = Math.floor((diffMs % 86400000) / 3600000);
                            const mins = Math.floor((diffMs % 3600000) / 60000);
                            if (days > 0) {
                              countdownText = `${days}d ${hours}h`;
                              countdownColor = days <= 2 ? 'text-amber-400' : 'text-emerald-400';
                              countdownBg = days <= 2 ? 'bg-amber-900/40' : 'bg-emerald-900/40';
                            } else if (hours > 0) {
                              countdownText = `${hours}h ${mins}m`;
                              countdownColor = 'text-amber-400';
                              countdownBg = 'bg-amber-900/40';
                            } else {
                              countdownText = `${mins}m`;
                              countdownColor = 'text-red-400';
                              countdownBg = 'bg-red-900/40';
                            }
                          }
                        }
                        // Extract short label from question
                        const extractLabel = (question) => {
                          const match = question.match(/["\u201c]([^"\u201d]+)["\u201d]/);
                          return match ? match[1] : question.replace(/^Will\s+/i, '').substring(0, 40);
                        };

                        return (
                          <div key={ev.event_id} className="bg-zinc-800 rounded-lg border border-zinc-700 overflow-hidden">
                            {/* Event header — clickable to expand */}
                            <div className="flex items-center gap-3 p-3">
                              <button
                                onClick={() => setMStockExpandedEvent(isExpanded ? null : ev.event_id)}
                                className="flex items-center gap-3 flex-1 min-w-0 text-left hover:opacity-80 transition"
                                data-testid={`mstock-event-${ev.event_id}`}
                              >
                                {ev.event_image && <img src={ev.event_image} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />}
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-semibold text-white">{ev.event_title}</p>
                                  <div className="flex items-center gap-2 mt-0.5">
                                    <span className="text-[10px] text-gray-500">{ev.markets.length} outcome{ev.markets.length !== 1 ? 's' : ''}</span>
                                    {countdownText && (
                                      <span className={`text-[10px] font-medium ${countdownColor} flex items-center gap-0.5`}>
                                        <Clock className="h-2.5 w-2.5" />{countdownText}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <ChevronRight className={`h-4 w-4 text-gray-500 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`} />
                              </button>
                              {/* Create button at event level */}
                              <Button
                                onClick={() => createMStockGameFromEvent(ev)}
                                disabled={mStockCreating === ev.event_id || isEventCreated}
                                size="sm"
                                className={`text-xs gap-1 px-3 py-1.5 h-auto flex-shrink-0 ${
                                  isEventCreated
                                    ? 'bg-zinc-700 text-gray-500 cursor-not-allowed'
                                    : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                                }`}
                                data-testid={`mstock-create-event-${ev.event_id}`}
                              >
                                {mStockCreating === ev.event_id ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> :
                                 isEventCreated ? 'Active' : <><Plus className="h-3.5 w-3.5" />Create</>}
                              </Button>
                            </div>

                            {/* Expanded event detail — Polymarket-style */}
                            {isExpanded && (
                              <div className="border-t border-zinc-700 bg-zinc-900/60">
                                {/* Countdown & meta bar */}
                                <div className="px-4 py-2.5 flex items-center gap-3 border-b border-zinc-800/60">
                                  {countdownText && (
                                    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${countdownBg} ${countdownColor}`} data-testid={`mstock-countdown-${ev.event_id}`}>
                                      <Clock className="h-3.5 w-3.5" />Ends in {countdownText}
                                    </span>
                                  )}
                                  <span className="text-[11px] text-gray-500">${Math.round(ev.total_volume || 0).toLocaleString()} Vol</span>
                                </div>

                                {/* Ranked outcomes — Polymarket-style rows */}
                                <div className="divide-y divide-zinc-800/60">
                                  {ev.markets.map((m, idx) => {
                                    const yesPct = (m.outcome_prices[0] || 0) * 100;
                                    const yesPctDisplay = yesPct < 1 && yesPct > 0 ? '<1' : Math.round(yesPct);
                                    const label = extractLabel(m.question);
                                    const vol = Math.round(parseFloat(m.volume || 0));
                                    return (
                                      <div key={m.market_id} className="flex items-center gap-3 px-4 py-3 hover:bg-zinc-800/40 transition" data-testid={`mstock-outcome-${m.market_id}`}>
                                        {/* Movie poster */}
                                        {m.image && <img src={m.image} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />}
                                        {/* Movie name & volume */}
                                        <div className="flex-1 min-w-0">
                                          <p className="text-sm font-semibold text-white truncate">{label}</p>
                                          <p className="text-[11px] text-gray-500 mt-0.5">${vol.toLocaleString()} Vol.</p>
                                        </div>
                                        {/* Percentage — large like Polymarket */}
                                        <span className={`text-xl font-bold flex-shrink-0 ${
                                          yesPct >= 50 ? 'text-emerald-400' : yesPct >= 10 ? 'text-white' : 'text-gray-500'
                                        }`}>
                                          {yesPctDisplay}%
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>

                                {/* Rules section */}
                                {ev.event_description && (
                                  <div className="border-t border-zinc-800/60 px-4 py-3">
                                    <p className="text-xs font-semibold text-gray-400 mb-1.5">Rules</p>
                                    <p className="text-[11px] text-gray-500 leading-relaxed whitespace-pre-line">{ev.event_description}</p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Active Movie Xchange Games */}
                <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-4 space-y-3">
                  <h4 className="text-sm font-semibold text-white flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-emerald-400" />Active Games ({mStockGames.filter(g => g.status === 'active').length})
                  </h4>
                  {mStockGames.filter(g => g.status === 'active').length === 0 ? (
                    <p className="text-xs text-gray-500 py-2">No active Movie Xchange games. Search and create one above.</p>
                  ) : mStockGames.filter(g => g.status === 'active').map(game => (
                    <div key={game.id} className="bg-zinc-800 rounded-lg p-3 border border-zinc-700 space-y-2" data-testid={`mstock-game-${game.id}`}>
                      <div className="flex items-start gap-3">
                        {game.image && <img src={game.image} alt="" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-white">{game.title}</p>
                          <div className="flex items-center gap-3 mt-1 text-[10px] text-gray-400">
                            <span>{game.total_entries} entries</span>
                            <span>{game.outcomes?.length} outcomes</span>
                            <span>{game.points_per_user || 5} pts/user</span>
                          </div>
                        </div>
                      </div>
                      {/* Outcome resolve buttons */}
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-[10px] text-gray-500 mr-1">Resolve:</span>
                        {game.outcomes.map((o, idx) => (
                          <Button
                            key={o}
                            onClick={() => resolveMStockGame(game.id, o)}
                            disabled={mStockResolving === game.id}
                            size="sm"
                            variant="outline"
                            className="text-[10px] gap-1 border-emerald-700/40 text-emerald-400 hover:bg-emerald-900/30 px-2 py-0.5 h-auto"
                            data-testid={`mstock-resolve-${game.id}-${idx}`}
                          >
                            <Trophy className="h-3 w-3" />{o}
                          </Button>
                        ))}
                        <Button
                          onClick={() => deleteMStockGame(game.id)}
                          size="sm" variant="outline"
                          className="text-[10px] gap-1 border-red-700/40 text-red-400 hover:bg-red-900/30 ml-auto px-2 py-0.5 h-auto"
                        >
                          <Trash2 className="h-3 w-3" />Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Resolved Games */}
                {mStockGames.filter(g => g.status === 'resolved').length > 0 && (
                  <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-4 space-y-3">
                    <h4 className="text-sm font-semibold text-white flex items-center gap-2">
                      <Trophy className="h-4 w-4 text-amber-400" />Resolved Games ({mStockGames.filter(g => g.status === 'resolved').length})
                    </h4>
                    {mStockGames.filter(g => g.status === 'resolved').map(game => (
                      <div key={game.id} className="bg-zinc-800 rounded-lg p-3 border border-zinc-700 flex items-center gap-3">
                        {game.image && <img src={game.image} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-white truncate">{game.title}</p>
                          <p className="text-[10px] text-emerald-400 font-semibold">Winner: {game.winning_outcome}</p>
                        </div>
                        <span className="text-[10px] text-gray-500">{game.total_entries} entries</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* GAME HISTORY */}
            {gameSubTab === 'history' && (
              <div className="space-y-2">
                {gameHistory.map(g => (
                  <div key={g.date} className="bg-zinc-900 rounded-lg border border-zinc-800 p-3 flex items-center gap-3">
                    {g.poster_path && <img src={`https://image.tmdb.org/t/p/w92${g.poster_path}`} alt="" className="w-12 h-16 rounded object-cover" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">{g.title}</p>
                      <p className="text-xs text-gray-500">{g.date} &middot; {(g.genres || []).join(', ')}</p>
                    </div>
                    <div className="flex gap-3 text-xs text-gray-400">
                      <span>{g.total_players} players</span>
                      <span className="text-green-400">{g.winners} won</span>
                      <span>avg {g.avg_score}</span>
                    </div>
                  </div>
                ))}
                {gameHistory.length === 0 && <p className="text-center text-gray-500 py-6">No game history yet</p>}
              </div>
            )}

            {/* LEADERBOARD */}
            {gameSubTab === 'leaderboard' && (
              <div className="space-y-3">
                <div className="flex gap-2">
                  {['daily', 'weekly', 'monthly', 'all'].map(p => (
                    <button key={p} onClick={() => { setLeaderboardPeriod(p); loadLeaderboard(p); }}
                      className={`px-3 py-1 rounded-lg text-xs font-medium ${leaderboardPeriod === p ? 'bg-yellow-600 text-white' : 'bg-zinc-800 text-gray-400 hover:bg-zinc-700'}`}>
                      {p.charAt(0).toUpperCase() + p.slice(1)}
                    </button>
                  ))}
                </div>
                <div className="bg-zinc-900 rounded-xl border border-zinc-800 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-zinc-800 text-gray-500 text-xs">
                      <th className="p-2 text-left">#</th><th className="p-2 text-left">Player</th>
                      <th className="p-2 text-right">Score</th><th className="p-2 text-right">Games</th>
                      <th className="p-2 text-right">Wins</th><th className="p-2 text-right">Win%</th>
                      <th className="p-2 text-right">Points</th><th className="p-2 text-right">Actions</th>
                    </tr></thead>
                    <tbody>
                      {gameLeaderboard.map((p, i) => (
                        <tr key={p.user_id} className="border-b border-zinc-800/50 hover:bg-zinc-800/40">
                          <td className="p-2 text-gray-400">{i+1}</td>
                          <td className="p-2">
                            <p className="text-white text-sm">{p.display_name}</p>
                            <p className="text-gray-500 text-xs">{p.email}</p>
                          </td>
                          <td className="p-2 text-right text-yellow-400 font-medium">{p.total_score}</td>
                          <td className="p-2 text-right text-gray-400">{p.games_played}</td>
                          <td className="p-2 text-right text-green-400">{p.wins}</td>
                          <td className="p-2 text-right text-gray-400">{p.win_rate}%</td>
                          <td className="p-2 text-right text-purple-400">{p.casita_points}</td>
                          <td className="p-2 text-right">
                            <div className="flex gap-1 justify-end">
                              <button onClick={() => {
                                const pts = prompt(`Adjust points for ${p.display_name}. Enter positive or negative number:`, '0');
                                if (pts && parseInt(pts) !== 0) {
                                  const reason = prompt('Reason:') || 'Admin adjustment';
                                  handleAdjustPoints(p.user_id, parseInt(pts), reason);
                                }
                              }} title="Adjust points" className="p-1 hover:bg-zinc-700 rounded" data-testid={`adjust-pts-${p.user_id}`}>
                                <Zap className="h-3.5 w-3.5 text-yellow-400" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {gameLeaderboard.length === 0 && <p className="text-center text-gray-500 py-6">No leaderboard data</p>}
                </div>
              </div>
            )}


            {/* WINNERS CIRCLE */}
            {gameSubTab === 'winners' && (
              <div className="space-y-4" data-testid="admin-winners-circle">
                {winnersLoading ? (
                  <div className="text-center py-8 text-gray-500">Loading winners...</div>
                ) : !winnersCircle ? (
                  <div className="text-center py-8 text-gray-500">No winners data available</div>
                ) : (() => {
                  const COLOR_MAP = {
                    yesterday: {
                      tabActive: 'bg-amber-600 text-white',
                      border: 'border-amber-800/30',
                      text: 'text-amber-400',
                      avatarBg: 'bg-amber-900/30 border-amber-700/40',
                      btn: 'bg-amber-600 hover:bg-amber-700',
                    },
                    last_week: {
                      tabActive: 'bg-blue-600 text-white',
                      border: 'border-blue-800/30',
                      text: 'text-blue-400',
                      avatarBg: 'bg-blue-900/30 border-blue-700/40',
                      btn: 'bg-blue-600 hover:bg-blue-700',
                    },
                    last_month: {
                      tabActive: 'bg-purple-600 text-white',
                      border: 'border-purple-800/30',
                      text: 'text-purple-400',
                      avatarBg: 'bg-purple-900/30 border-purple-700/40',
                      btn: 'bg-purple-600 hover:bg-purple-700',
                    },
                  };
                  const periods = [
                    { key: 'yesterday', label: 'Yesterday', dataKey: 'yesterday', period: 'Daily' },
                    { key: 'last_week', label: 'Last Week', dataKey: 'last_week', period: 'Weekly' },
                    { key: 'last_month', label: 'Last Month', dataKey: 'last_month', period: 'Monthly' },
                  ];
                  const activeKey = winnersTab || 'yesterday';
                  const activeP = periods.find(p => p.key === activeKey) || periods[0];
                  const winner = winnersCircle[activeP.dataKey];
                  const colors = COLOR_MAP[activeP.key];

                  return (
                    <>
                      {/* Period sub-tabs */}
                      <div className="flex gap-2" data-testid="winners-period-tabs">
                        {periods.map(p => (
                          <button
                            key={p.key}
                            onClick={() => setWinnersTab(p.key)}
                            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition ${
                              activeP.key === p.key
                                ? COLOR_MAP[p.key].tabActive
                                : 'bg-zinc-800 text-gray-400 hover:bg-zinc-700'
                            }`}
                            data-testid={`winners-tab-${p.key}`}
                          >
                            <Crown className="h-3.5 w-3.5" />{p.label}
                          </button>
                        ))}
                      </div>

                      {/* Winner card for selected period */}
                      <div className={`bg-zinc-900 rounded-xl border ${colors.border} p-5 space-y-4`} data-testid={`winner-${activeP.key}`}>
                        <div className="flex items-center gap-2">
                          <Crown className={`h-5 w-5 ${colors.text}`} />
                          <h4 className={`text-base font-semibold ${colors.text}`}>{activeP.label}'s Winner</h4>
                        </div>

                        {winner && winner.user_id ? (
                          <div className="space-y-3">
                            <div className="flex items-center gap-3">
                              <div className={`w-12 h-12 rounded-full ${colors.avatarBg} border-2 flex items-center justify-center`}>
                                <Trophy className={`h-6 w-6 ${colors.text}`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-base font-semibold text-white truncate">{winner.name || 'Unknown'}</p>
                                <p className="text-xs text-gray-400 truncate flex items-center gap-1">
                                  <Mail className="h-3 w-3 flex-shrink-0" />{winner.email || 'No email'}
                                </p>
                              </div>
                            </div>

                            <div className="grid grid-cols-3 gap-3">
                              <div className="bg-zinc-800 rounded-lg p-2.5 text-center">
                                <p className="text-lg font-bold text-white">{winner.total_score || 0}</p>
                                <p className="text-[10px] text-gray-500">Score</p>
                              </div>
                              <div className="bg-zinc-800 rounded-lg p-2.5 text-center">
                                <p className="text-lg font-bold text-white">{winner.games_won || 0}</p>
                                <p className="text-[10px] text-gray-500">Won</p>
                              </div>
                              <div className="bg-zinc-800 rounded-lg p-2.5 text-center">
                                <p className="text-lg font-bold text-white">{winner.games_played || 0}</p>
                                <p className="text-[10px] text-gray-500">Played</p>
                              </div>
                            </div>

                            <Button
                              onClick={() => handleAwardWinner(winner.user_id, activeP.period)}
                              disabled={awardingUser === winner.user_id}
                              className={`w-full ${colors.btn} text-white gap-1.5`}
                              data-testid={`award-${activeP.key}-btn`}
                            >
                              {awardingUser === winner.user_id ? (
                                <div className="animate-spin h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full" />
                              ) : (
                                <Award className="h-4 w-4" />
                              )}
                              Send {activeP.period} Award Email
                            </Button>
                          </div>
                        ) : (
                          <div className="text-center py-6">
                            <Trophy className="h-8 w-8 text-zinc-700 mx-auto mb-2" />
                            <p className="text-sm text-gray-500">No winner for this period yet</p>
                          </div>
                        )}
                      </div>

                      {/* Recent Daily Winners History (shown under Yesterday tab) */}
                      {activeP.key === 'yesterday' && winnersCircle.recent_daily_winners && winnersCircle.recent_daily_winners.length > 0 && (
                        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-4 space-y-3">
                          <h4 className="text-sm font-semibold text-white flex items-center gap-2">
                            <Clock className="h-4 w-4 text-gray-400" />Previous Daily Winners
                          </h4>
                          <div className="space-y-1.5">
                            {winnersCircle.recent_daily_winners.map((w, i) => (
                              <div key={i} className="flex items-center justify-between p-2.5 bg-zinc-800 rounded-lg">
                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                  <div className="text-[11px] text-gray-500 font-mono w-20 flex-shrink-0">{w.date}</div>
                                  <div className="min-w-0 flex-1">
                                    <p className="text-xs text-white font-medium truncate">{w.name}</p>
                                    <p className="text-[10px] text-gray-500 truncate">{w.email || 'No email'}</p>
                                  </div>
                                  <div className="text-xs text-amber-400 font-semibold flex-shrink-0">{w.total_score} pts</div>
                                </div>
                                {w.user_id && (
                                  <Button
                                    onClick={() => handleAwardWinner(w.user_id, `Daily (${w.date})`)}
                                    disabled={awardingUser === w.user_id}
                                    size="sm"
                                    variant="outline"
                                    className="ml-2 border-amber-700/40 text-amber-400 hover:bg-amber-900/30 text-[10px] px-2 py-1 h-auto gap-1"
                                    data-testid={`award-history-${i}`}
                                  >
                                    <Award className="h-3 w-3" />Award
                                  </Button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Prize Records — Weekly/Monthly winners for admin prize tracking */}
                      {winnerRecords.length > 0 && (
                        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-4 space-y-3" data-testid="prize-records">
                          <h4 className="text-sm font-semibold text-white flex items-center gap-2">
                            <Award className="h-4 w-4 text-emerald-400" />Prize Records (Auto-Tracked)
                          </h4>
                          <p className="text-[10px] text-gray-500">Automated weekly/monthly winners. Mark as sent after emailing prize.</p>
                          <div className="space-y-1.5">
                            {winnerRecords.map(rec => (
                              <div key={rec.id} className={`flex items-center justify-between p-2.5 rounded-lg ${rec.prize_sent ? 'bg-zinc-800/50' : 'bg-zinc-800'}`}>
                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                                    rec.period === 'monthly' ? 'bg-purple-900/40 text-purple-400' : 'bg-blue-900/40 text-blue-400'
                                  }`}>{rec.period}</span>
                                  <div className="min-w-0 flex-1">
                                    <p className="text-xs text-white font-medium truncate">{rec.winner?.name || 'Unknown'}</p>
                                    <p className="text-[10px] text-gray-500 truncate">{rec.winner?.email} &middot; {rec.start_date} to {rec.end_date}</p>
                                  </div>
                                  <div className="text-xs text-amber-400 font-semibold flex-shrink-0">{rec.winner?.total_score || 0} pts</div>
                                </div>
                                {rec.prize_sent ? (
                                  <span className="text-[10px] text-emerald-400 font-medium ml-2 flex items-center gap-0.5"><CheckCircle2 className="h-3 w-3" />Sent</span>
                                ) : (
                                  <Button
                                    onClick={() => handleMarkPrizeSent(rec.id)}
                                    size="sm" variant="outline"
                                    className="ml-2 border-emerald-700/40 text-emerald-400 hover:bg-emerald-900/30 text-[10px] px-2 py-1 h-auto gap-1"
                                  >
                                    <Award className="h-3 w-3" />Mark Sent
                                  </Button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}


            {/* FAIRNESS FLAGS */}
            {gameSubTab === 'fairness' && (
              <div className="space-y-2">
                {fairnessFlags.length === 0 ? (
                  <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6 text-center">
                    <Shield className="h-10 w-10 text-green-400 mx-auto mb-2" />
                    <p className="text-green-400 font-medium">No suspicious activity detected</p>
                    <p className="text-gray-500 text-sm mt-1">All players appear to be playing fairly</p>
                  </div>
                ) : (
                  fairnessFlags.map((f, i) => (
                    <div key={i} className={`bg-zinc-900 rounded-lg border p-3 ${f.severity === 'high' ? 'border-red-800/60' : 'border-yellow-800/60'}`}>
                      <div className="flex items-start gap-2">
                        <AlertTriangle className={`h-4 w-4 mt-0.5 flex-shrink-0 ${f.severity === 'high' ? 'text-red-400' : 'text-yellow-400'}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${f.severity === 'high' ? 'bg-red-900/50 text-red-300' : 'bg-yellow-900/50 text-yellow-300'}`}>
                              {f.severity.toUpperCase()}
                            </span>
                            <span className="text-xs text-gray-400 font-mono">{f.type}</span>
                          </div>
                          <p className="text-sm text-white mt-1">{f.detail}</p>
                          {f.user && <p className="text-xs text-gray-500 mt-0.5">{f.user.display_name} ({f.user.email})</p>}
                          {f.users && <p className="text-xs text-gray-500 mt-0.5">Accounts: {f.users.map(u => u.display_name || u.id).join(', ')}</p>}
                          {f.type === 'shared_ip' && <p className="text-xs text-gray-500 flex items-center gap-1"><Globe className="h-3 w-3" />{f.ip}</p>}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}


        {/* ==================== PRIZES TAB ==================== */}
        {activeTab === 'prizes' && (
          <div className="space-y-6" data-testid="admin-prizes">
            {/* Award Prize Form */}
            <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-5 space-y-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2"><Award className="h-5 w-5 text-amber-400" />Award Monthly Prize</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* User Search */}
                <div className="sm:col-span-2">
                  <label className="text-xs text-gray-400 block mb-1">Winner User *</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={prizeForm.user_display || prizeUserSearch}
                      onChange={(e) => { searchPrizeUsers(e.target.value); setPrizeForm(f => ({ ...f, user_id: '', user_display: '' })); }}
                      placeholder="Search by name, email, or username..."
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-600"
                      data-testid="prize-user-search"
                    />
                    {prizeUserResults.length > 0 && !prizeForm.user_id && (
                      <div className="absolute z-20 w-full mt-1 bg-zinc-800 border border-zinc-700 rounded-lg max-h-40 overflow-y-auto shadow-xl">
                        {prizeUserResults.map(u => (
                          <button key={u.id} onClick={() => {
                            const name = u.username ? `@${u.username}` : `${u.first_name || ''} ${u.last_name || ''}`.trim();
                            setPrizeForm(f => ({ ...f, user_id: u.id, user_display: `${name} (${u.email})` }));
                            setPrizeUserResults([]);
                            setPrizeUserSearch('');
                          }} className="w-full text-left px-3 py-2 hover:bg-zinc-700 flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-zinc-600 flex items-center justify-center text-[10px] text-white font-semibold overflow-hidden flex-shrink-0">
                              {u.profile_picture ? <img src={u.profile_picture} alt="" className="w-full h-full object-cover" /> : (u.first_name?.[0] || '?')}
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs text-white truncate">{u.username ? `@${u.username}` : `${u.first_name || ''} ${u.last_name || ''}`}</p>
                              <p className="text-[10px] text-gray-500 truncate">{u.email}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Month */}
                <div>
                  <label className="text-xs text-gray-400 block mb-1">Month / Period *</label>
                  <input type="month" value={prizeForm.month} onChange={e => setPrizeForm(f => ({ ...f, month: e.target.value }))}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-600" data-testid="prize-month" />
                </div>

                {/* Placement */}
                <div>
                  <label className="text-xs text-gray-400 block mb-1">Placement *</label>
                  <select value={prizeForm.placement} onChange={e => {
                    const p = e.target.value;
                    setPrizeForm(f => ({ ...f, placement: p, gift_card_value: PRIZE_TEMPLATES[p] || f.gift_card_value }));
                  }}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-600" data-testid="prize-placement">
                    <option value="">Select placement...</option>
                    <option value="1ST">1st Place</option>
                    <option value="2ND">2nd Place</option>
                    <option value="3RD">3rd Place</option>
                  </select>
                </div>

                {/* Gift Card ID + PIN */}
                <div>
                  <label className="text-xs text-gray-400 block mb-1">Gift Card ID / Code *</label>
                  <input type="text" value={prizeForm.gift_card_id} onChange={e => setPrizeForm(f => ({ ...f, gift_card_id: e.target.value }))}
                    placeholder="e.g., AMZN-XXXX-YYYY" className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-600" data-testid="prize-gc-id" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-400 block mb-1">PIN (optional)</label>
                    <input type="text" value={prizeForm.gift_card_pin} onChange={e => setPrizeForm(f => ({ ...f, gift_card_pin: e.target.value }))}
                      placeholder="e.g., 1234" className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-600" data-testid="prize-gc-pin" />
                  </div>

                  {/* Gift Card Value */}
                  <div>
                    <label className="text-xs text-gray-400 block mb-1">Gift Card Value ($) *</label>
                    <input type="number" min="0.01" step="0.01" value={prizeForm.gift_card_value} onChange={e => setPrizeForm(f => ({ ...f, gift_card_value: e.target.value }))}
                      placeholder="50.00" className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-600" data-testid="prize-gc-value" />
                  </div>
                </div>

                {/* Notes */}
                <div className="sm:col-span-2">
                  <label className="text-xs text-gray-400 block mb-1">Notes (optional)</label>
                  <input type="text" value={prizeForm.notes} onChange={e => setPrizeForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="Optional notes..." className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-600" />
                </div>

                {/* Override toggle */}
                <div className="sm:col-span-2 flex items-center gap-2">
                  <input type="checkbox" checked={prizeForm.override} onChange={e => setPrizeForm(f => ({ ...f, override: e.target.checked }))}
                    className="rounded border-zinc-700 bg-zinc-800 text-amber-600" data-testid="prize-override" />
                  <label className="text-xs text-gray-400">Override existing placement (admin-only)</label>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button onClick={handleAwardPrize} disabled={prizeAwarding} className="bg-amber-600 hover:bg-amber-700 text-white gap-1.5" data-testid="prize-award-btn">
                  {prizeAwarding ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Award className="h-4 w-4" />}
                  {prizeAwarding ? 'Awarding...' : 'Award Prize'}
                </Button>
                <Button variant="outline" onClick={() => { const rm = new Date(); rm.setMonth(rm.getMonth() - 1); setPrizeForm({ user_id: '', user_display: '', month: rm.toISOString().slice(0, 7), placement: '', gift_card_id: '', gift_card_pin: '', gift_card_value: '', notes: '', override: false }); }} className="border-zinc-700 text-gray-400">
                  Cancel
                </Button>
              </div>
            </div>

            {/* Auto-populate from Leaderboard */}
            <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-5 space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="text-lg font-bold text-white flex items-center gap-2"><Users className="h-5 w-5 text-blue-400" />Auto-fill from Leaderboard</h3>
                <Button onClick={handleAutoPopulate} disabled={loadingDrafts} className="bg-blue-600 hover:bg-blue-700 text-white text-xs gap-1.5" data-testid="auto-populate-btn">
                  {loadingDrafts ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Trophy className="h-3.5 w-3.5" />}
                  {loadingDrafts ? 'Loading...' : `Load Top 3 for ${prizeForm.month}`}
                </Button>
              </div>
              <p className="text-[11px] text-gray-500">Pulls top 3 Film in Five scorers for the selected month. Fill in gift card codes and click Award.</p>

              {prizeDrafts.length > 0 && (
                <div className="space-y-2">
                  {prizeDrafts.map(draft => {
                    const placementColors = { '1ST': 'text-amber-400 bg-amber-900/30 border-amber-800/40', '2ND': 'text-gray-300 bg-zinc-700 border-zinc-600', '3RD': 'text-orange-400 bg-orange-900/30 border-orange-800/40' };
                    const placementLabels = { '1ST': '1st', '2ND': '2nd', '3RD': '3rd' };
                    return (
                      <div key={draft.placement} className={`rounded-lg border p-3 space-y-2 ${draft.already_awarded ? 'bg-zinc-800/30 border-zinc-800 opacity-60' : 'bg-zinc-800 border-zinc-700'}`} data-testid={`draft-${draft.placement}`}>
                        <div className="flex items-center gap-3">
                          <span className={`text-xs font-bold px-2 py-0.5 rounded border ${placementColors[draft.placement]}`}>{placementLabels[draft.placement]}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white font-medium truncate">{draft.user_display}</p>
                            <p className="text-[10px] text-gray-500">{draft.total_score} pts &middot; {draft.games_played} games &middot; {draft.games_won} wins</p>
                          </div>
                          <span className="text-sm font-bold text-emerald-400">${draft.default_value}</span>
                        </div>
                        {draft.already_awarded ? (
                          <p className="text-[10px] text-emerald-400 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />Already awarded for this month</p>
                        ) : (
                          <div className="flex gap-2 items-center">
                            <input
                              type="text"
                              placeholder="Gift Card Code *"
                              className="flex-1 bg-zinc-700 border border-zinc-600 rounded-lg px-2.5 py-1.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-amber-600"
                              data-testid={`draft-gc-${draft.placement}`}
                              id={`draft-gc-${draft.placement}`}
                            />
                            <Button
                              onClick={() => handleAwardDraft(draft, document.getElementById(`draft-gc-${draft.placement}`).value)}
                              disabled={prizeAwarding}
                              size="sm"
                              className="bg-amber-600 hover:bg-amber-700 text-white text-xs gap-1 h-8 px-3"
                            >
                              <Award className="h-3 w-3" />Award
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Prize Records Table */}
            <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-5 space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="text-lg font-bold text-white flex items-center gap-2"><Trophy className="h-5 w-5 text-amber-400" />Prize Records</h3>
                <div className="flex gap-2 flex-wrap">
                  <input type="month" value={prizeFilter.month} onChange={e => setPrizeFilter(f => ({ ...f, month: e.target.value }))}
                    className="bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1 text-xs text-white" placeholder="Filter month" />
                  <select value={prizeFilter.placement} onChange={e => setPrizeFilter(f => ({ ...f, placement: e.target.value }))}
                    className="bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1 text-xs text-white">
                    <option value="">All Placements</option>
                    <option value="1ST">1st Place</option>
                    <option value="2ND">2nd Place</option>
                    <option value="3RD">3rd Place</option>
                  </select>
                  <select value={prizeFilter.status} onChange={e => setPrizeFilter(f => ({ ...f, status: e.target.value }))}
                    className="bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1 text-xs text-white">
                    <option value="">All Statuses</option>
                    <option value="AWARDED">Awarded</option>
                    <option value="SENT">Sent</option>
                    <option value="CLAIMED">Claimed</option>
                    <option value="CANCELLED">Cancelled</option>
                  </select>
                </div>
              </div>

              {prizes.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Award className="h-10 w-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No prize records found</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {prizes.map(p => {
                    const placementColors = { '1ST': 'text-amber-400 bg-amber-900/30', '2ND': 'text-gray-300 bg-zinc-700', '3RD': 'text-orange-400 bg-orange-900/30' };
                    const statusColors = { 'AWARDED': 'text-blue-400 bg-blue-900/30', 'SENT': 'text-emerald-400 bg-emerald-900/30', 'CLAIMED': 'text-purple-400 bg-purple-900/30', 'CANCELLED': 'text-red-400 bg-red-900/30' };
                    return (
                      <div key={p.id} className="bg-zinc-800 rounded-lg p-3 flex items-center gap-3 flex-wrap" data-testid={`prize-record-${p.id}`}>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${placementColors[p.placement] || 'text-gray-400'}`}>{p.placement === '1ST' ? '1st' : p.placement === '2ND' ? '2nd' : '3rd'}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-white font-medium truncate">{p.user?.username ? `@${p.user.username}` : `${p.user?.first_name || ''} ${p.user?.last_name || ''}`.trim() || 'Unknown'}</p>
                          <p className="text-[10px] text-gray-500 truncate">{p.user?.email} &middot; {p.month}</p>
                        </div>
                        <span className="text-sm font-bold text-emerald-400">${p.gift_card_value}</span>
                        <span className={`text-[10px] font-medium px-2 py-0.5 rounded ${statusColors[p.status] || ''}`}>{p.status}</span>
                        {p.status === 'AWARDED' && (
                          <Button size="sm" onClick={() => handleUpdatePrizeStatus(p.id, 'SENT')} className="bg-emerald-600 hover:bg-emerald-700 text-[10px] h-7 px-2 gap-1">
                            <Mail className="h-3 w-3" />Mark Sent
                          </Button>
                        )}
                        {p.status === 'SENT' && (
                          <Button size="sm" onClick={() => handleUpdatePrizeStatus(p.id, 'CLAIMED')} variant="outline" className="border-purple-700/40 text-purple-400 text-[10px] h-7 px-2 gap-1">
                            <CheckCircle2 className="h-3 w-3" />Mark Claimed
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ==================== USERS TAB ==================== */}
        {activeTab === 'users' && (
          <div className="space-y-4" data-testid="admin-users">
            {/* Search Bar */}
            <div className="flex items-center gap-3">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search users by name, email, or username..."
                  className="bg-zinc-900 border-zinc-800 text-white pl-10"
                  data-testid="admin-user-search"
                />
              </div>
              <span className="text-gray-500 text-sm">
                {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}
              </span>
            </div>

            {/* Users Table */}
            <div className="bg-zinc-900 rounded-xl border border-zinc-800 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="admin-users-table">
                  <thead>
                    <tr className="border-b border-zinc-800 bg-zinc-900/80">
                      <th className="text-left py-3 px-4 text-gray-500 font-medium text-xs uppercase tracking-wider">User</th>
                      <th className="text-left py-3 px-4 text-gray-500 font-medium text-xs uppercase tracking-wider">Email</th>
                      <th className="text-left py-3 px-4 text-gray-500 font-medium text-xs uppercase tracking-wider">Role</th>
                      <th className="text-left py-3 px-4 text-gray-500 font-medium text-xs uppercase tracking-wider">Status</th>
                      <th className="text-left py-3 px-4 text-gray-500 font-medium text-xs uppercase tracking-wider">Joined</th>
                      <th className="text-right py-3 px-4 text-gray-500 font-medium text-xs uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center py-12 text-gray-600">
                          {searchQuery ? 'No users match your search' : 'No users registered yet'}
                        </td>
                      </tr>
                    ) : (
                      filteredUsers.map(user => (
                        <tr key={user.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors" data-testid={`user-row-${user.id}`}>
                          <td className="py-3 px-4">
                            <button
                              onClick={() => setSelectedUserId(user.id)}
                              className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                              data-testid={`view-user-${user.id}`}
                            >
                              <div className="w-9 h-9 rounded-full bg-red-600 flex items-center justify-center text-white text-sm font-medium overflow-hidden flex-shrink-0">
                                {user.profile_picture ? (
                                  <img src={user.profile_picture} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`
                                )}
                              </div>
                              <div className="text-left">
                                <p className="text-white font-medium text-sm">{user.first_name} {user.last_name}</p>
                                <p className="text-gray-600 text-xs">@{user.username || '—'}</p>
                              </div>
                            </button>
                          </td>
                          <td className="py-3 px-4 text-gray-400 text-sm">{user.email}</td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              user.role === 'admin'
                                ? 'bg-purple-600/20 text-purple-400'
                                : 'bg-blue-600/20 text-blue-400'
                            }`}>
                              {user.role || 'user'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                              user.is_active !== false
                                ? 'bg-green-600/15 text-green-400'
                                : 'bg-red-600/15 text-red-400'
                            }`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${user.is_active !== false ? 'bg-green-400' : 'bg-red-400'}`} />
                              {user.is_active !== false ? 'Active' : 'Disabled'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-gray-500 text-sm">
                            {new Date(user.created_at).toLocaleDateString()}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setSelectedUserId(user.id)}
                                className="text-gray-400 hover:text-white h-8 w-8 p-0"
                                title="View Profile"
                                data-testid={`view-profile-${user.id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              {user.role !== 'admin' && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleToggleUserStatus(user.id)}
                                    className={`h-8 w-8 p-0 ${
                                      user.is_active !== false
                                        ? 'text-yellow-500 hover:text-yellow-400'
                                        : 'text-green-500 hover:text-green-400'
                                    }`}
                                    title={user.is_active !== false ? 'Disable User' : 'Enable User'}
                                    data-testid={`toggle-status-${user.id}`}
                                  >
                                    {user.is_active !== false ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setConfirmAction({
                                      type: 'user',
                                      id: user.id,
                                      name: `${user.first_name} ${user.last_name}`,
                                      action: () => handleDeleteUser(user.id)
                                    })}
                                    className="text-red-500 hover:text-red-400 h-8 w-8 p-0"
                                    title="Delete User"
                                    data-testid={`delete-user-${user.id}`}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </>
                              )}
                              {user.role === 'admin' && (
                                <span className="text-gray-600 text-xs flex items-center px-2">Protected</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ==================== CLUBS TAB ==================== */}
        {activeTab === 'clubs' && (
          <div className="space-y-4" data-testid="admin-clubs">
            <div className="flex items-center justify-between">
              <h2 className="text-white font-semibold text-lg">Club Management</h2>
              <span className="text-gray-500 text-sm">{clubs.length} clubs</span>
            </div>

            <div className="bg-zinc-900 rounded-xl border border-zinc-800 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="admin-clubs-table">
                  <thead>
                    <tr className="border-b border-zinc-800 bg-zinc-900/80">
                      <th className="text-left py-3 px-4 text-gray-500 font-medium text-xs uppercase tracking-wider">Club</th>
                      <th className="text-left py-3 px-4 text-gray-500 font-medium text-xs uppercase tracking-wider">Owner</th>
                      <th className="text-left py-3 px-4 text-gray-500 font-medium text-xs uppercase tracking-wider">Type</th>
                      <th className="text-left py-3 px-4 text-gray-500 font-medium text-xs uppercase tracking-wider">Members</th>
                      <th className="text-left py-3 px-4 text-gray-500 font-medium text-xs uppercase tracking-wider">Created</th>
                      <th className="text-right py-3 px-4 text-gray-500 font-medium text-xs uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clubs.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center py-12 text-gray-600">
                          No clubs created yet
                        </td>
                      </tr>
                    ) : (
                      clubs.map(club => (
                        <tr key={club.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors" data-testid={`club-row-${club.id}`}>
                          <td className="py-3 px-4">
                            <div>
                              <p className="text-white font-medium text-sm">{club.name}</p>
                              {club.description && (
                                <p className="text-gray-600 text-xs truncate max-w-[200px]">{club.description}</p>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-gray-400 text-sm">
                            {club.owner?.first_name} {club.owner?.last_name}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              club.is_private
                                ? 'bg-yellow-600/20 text-yellow-400'
                                : 'bg-green-600/20 text-green-400'
                            }`}>
                              {club.is_private ? 'Private' : 'Public'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <button
                              onClick={() => setSelectedClub(club)}
                              className="text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors"
                              data-testid={`view-members-${club.id}`}
                            >
                              {club.member_count} member{club.member_count !== 1 ? 's' : ''}
                            </button>
                          </td>
                          <td className="py-3 px-4 text-gray-500 text-sm">
                            {new Date(club.created_at).toLocaleDateString()}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setSelectedClub(club)}
                                className="text-gray-400 hover:text-white h-8 w-8 p-0"
                                title="View Members"
                              >
                                <Users className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setConfirmAction({
                                  type: 'club',
                                  id: club.id,
                                  name: club.name,
                                  action: () => handleDeleteClub(club.id)
                                })}
                                className="text-red-500 hover:text-red-400 h-8 w-8 p-0"
                                title="Delete Club"
                                data-testid={`delete-club-${club.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ==================== SETTINGS TAB ==================== */}
        {activeTab === 'settings' && (
          <div className="max-w-2xl space-y-6" data-testid="admin-settings">
            <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <User className="h-4 w-4 text-red-500" /> Admin Profile
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-500 text-xs">First Name</Label>
                    <p className="text-white mt-1">{adminUser?.first_name}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500 text-xs">Last Name</Label>
                    <p className="text-white mt-1">{adminUser?.last_name}</p>
                  </div>
                </div>
                <div>
                  <Label className="text-gray-500 text-xs">Email</Label>
                  <p className="text-white mt-1">{adminUser?.email}</p>
                </div>
                <div>
                  <Label className="text-gray-500 text-xs">Role</Label>
                  <p className="text-purple-400 font-medium mt-1">{adminUser?.role?.toUpperCase()}</p>
                </div>
                <div>
                  <Label className="text-gray-500 text-xs">Username</Label>
                  <p className="text-white mt-1">@{adminUser?.username || '—'}</p>
                </div>
              </div>
            </div>

            <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <Settings className="h-4 w-4 text-red-500" /> Quick Actions
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  onClick={() => setActiveTab('users')}
                  className="bg-zinc-800 hover:bg-zinc-700 text-white justify-start h-12"
                >
                  <Users className="h-4 w-4 mr-2" /> Manage Users
                </Button>
                <Button
                  onClick={() => setActiveTab('clubs')}
                  className="bg-zinc-800 hover:bg-zinc-700 text-white justify-start h-12"
                >
                  <Activity className="h-4 w-4 mr-2" /> Manage Clubs
                </Button>
                <Button
                  onClick={() => setActiveTab('overview')}
                  className="bg-zinc-800 hover:bg-zinc-700 text-white justify-start h-12"
                >
                  <BarChart3 className="h-4 w-4 mr-2" /> View Stats
                </Button>
                <a href="/">
                  <Button className="w-full bg-zinc-800 hover:bg-zinc-700 text-white justify-start h-12">
                    Back to <span style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '1px' }}>CASITAPLEX</span>
                  </Button>
                </a>
              </div>
            </div>

            <Button
              onClick={handleLogout}
              className="w-full bg-red-600 hover:bg-red-700 text-white h-12 text-base font-semibold"
              data-testid="settings-logout-btn"
            >
              <LogOut className="h-5 w-5 mr-2" /> Log Out of Admin Dashboard
            </Button>
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={!!confirmAction} onOpenChange={() => setConfirmAction(null)}>
        <DialogContent className="max-w-sm bg-zinc-900 text-white border-zinc-700" data-testid="confirm-dialog">
          <DialogTitle className="text-lg font-bold text-white flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            Confirm Delete
          </DialogTitle>
          <p className="text-gray-400 text-sm mt-2">
            {confirmAction?.type === 'user'
              ? `Delete user "${confirmAction?.name}" and ALL their data? This cannot be undone.`
              : `Delete club "${confirmAction?.name}" and all its posts? This cannot be undone.`
            }
          </p>
          <div className="flex justify-end gap-3 mt-6">
            <Button
              variant="outline"
              onClick={() => setConfirmAction(null)}
              className="border-zinc-700 text-gray-300 hover:text-white"
              data-testid="confirm-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={() => confirmAction?.action?.()}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete"
            >
              <Trash2 className="h-4 w-4 mr-2" /> Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* User Detail Modal */}
      <UserDetailModal
        userId={selectedUserId}
        isOpen={!!selectedUserId}
        onClose={() => setSelectedUserId(null)}
      />

      {/* Club Members Modal */}
      <ClubMembersModal
        club={selectedClub}
        isOpen={!!selectedClub}
        onClose={() => setSelectedClub(null)}
      />
    </div>
  );
};

export default AdminPage;
